export const TERMS_UPDATED = "24 August 2026";

export const TERMS_MD = `# Terms and Conditions — Pravadh GovernX

**Last updated: ${TERMS_UPDATED}**

These Terms and Conditions ("Terms") govern your access to and use of Pravadh GovernX ("GovernX," "the Service"), provided by Pravadh Labs ("we," "us," "our"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.

## 1. Description of Service

GovernX is a data extraction and governance platform from Pravadh Labs. It connects to third-party enterprise systems — including Salesforce, Zoho CRM, Zoho Books, Oracle Fusion, Oracle NetSuite, and Oracle EBS ("Connected Systems") — so you can extract data, monitor usage limits, apply governance policies, and keep a SHA-256 audit trail. Optional features include EMIE, the built-in co-pilot; bringing your own LLM; white-label branding; and click-to-deploy of private images to a customer server. You choose those later from the product — not at signup.

## 2. Eligibility and Account Registration

2.1 You must be at least 18 years old and have authority to bind your organization to these Terms.
2.2 New accounts must use a business email address. Personal inboxes such as Gmail, Outlook, Yahoo, iCloud, and similar consumer or disposable addresses are not accepted.
2.3 Signup asks only for your name, work email, and password. No payment method and no plan purchase are required to create an account.
2.4 You are responsible for keeping credentials confidential and for activity under your account.
2.5 You must verify your email before signing in. We may suspend accounts that remain unverified after a reasonable period.

## 3. Connected Systems and Third-Party Authorization

3.1 By connecting a third-party system, you represent that you have legal authority to grant GovernX access, including any permissions your organization requires.
3.2 You are responsible for complying with each Connected System's own terms.
3.3 GovernX accesses Connected Systems only to perform the extraction, governance, and monitoring you configure. We do not use that data for other purposes except as described in these Terms.
3.4 You may disconnect a system at any time. We then stop active extraction from it, subject to ordinary retention of logs and exports you already created.
3.5 Credentials and tokens for Connected Systems are stored with AES-256 encryption and are not shown in plaintext in the product.

## 4. Trial, Read-Only Access, and Billing

4.1 Every new account receives a 7-day trial with full use of the Service. No card is collected at signup.
4.2 On the eighth day, if there is no active paid subscription, the workspace stays available in read-only. You can still sign in and view existing connections, jobs, rules, and history. Creating or changing connections, running extractions or jobs, and using EMIE wait until you continue with a plan.
4.3 Plans (Starter, Classic, Enterprise, Custom) and prices are shown on the Pricing page and in Billing. You may choose or change a plan there after signup. Fees are billed in advance through our payment processor and are non-refundable except as required by law.
4.4 You may upgrade, downgrade, or cancel from Billing. Downgrades and cancellations take effect at the end of the current billing cycle.
4.5 We may change pricing with at least 30 days' notice to active subscribers.
4.6 Admin and superadmin accounts operated by Pravadh Labs are not subject to the trial lock described above.

## 5. Acceptable Use

You agree not to:
- Extract, store, or process data you do not have the legal right to access
- Circumvent usage limits, security measures, or authentication of the Service or any Connected System
- Violate any applicable law or the rights of any third party
- Reverse-engineer or attempt to extract the source code of the Service, except as permitted by law
- Use the Service to build a competing product
- Share account credentials with unauthorized users

## 6. Data Ownership and Security

6.1 You retain ownership of data extracted from your Connected Systems ("Customer Data"). We do not claim ownership of Customer Data.
6.2 We process Customer Data only to provide the Service.
6.3 We protect Customer Data and credentials with AES-256 encryption and record material actions on a SHA-256 audit chain.
6.4 If you enable a customer-owned language model for EMIE, that key is stored encrypted for your tenant. We do not use it for other customers.

## 7. Governance Alerts and Limitations

7.1 Usage monitoring, threshold alerts, and recommendations depend on Connected System APIs. We do not guarantee the accuracy or timeliness of third-party API data.
7.2 Alerts and EMIE forecasts are informational. They are not a guarantee against outages, data loss, or limit breaches in your Connected Systems.
7.3 You remain responsible for your own compliance with each Connected System's limits and policies.

## 8. EMIE Co-Pilot

8.1 EMIE is an optional assistant that explains and recommends based on your account data. Outputs are generated automatically and may be incomplete or incorrect.
8.2 Verify any important decision before you act on EMIE's output.
8.3 We do not guarantee EMIE's availability or fitness for a particular purpose. After the trial, EMIE is part of paid, full-access use.

## 9. White-Label and Customer Deployments

9.1 Custom-tier features may include your product name, logo, colors, domain, and a renamed assistant, plus deployment of private application images to servers you designate (including Linux VMs, Kubernetes, and other OCI runtimes).
9.2 Customer deployments do not receive application source code, and do not receive credentials from our hosted environment. You generate instance secrets on your servers. License and subscription status for those instances follow the same trial and billing rules as the hosted Service unless we agree otherwise in writing.

## 10. Service Availability

10.1 We aim for high availability but do not guarantee uninterrupted access. Maintenance, third-party outages, and events outside our control may affect the Service.
10.2 We are not liable for extraction, monitoring, or alerting failures caused by Connected System outages, rate limits, or API changes.

## 11. Intellectual Property

11.1 The Service, including its software, design, branding, and underlying technology, is owned by Pravadh Labs and protected by applicable law.
11.2 We grant you a limited, non-exclusive, non-transferable license to use the Service under these Terms for the duration of your trial or paid subscription.

## 12. Termination

12.1 You may stop using the Service and cancel from Billing at any time.
12.2 We may suspend or terminate access if you violate these Terms, fail to pay applicable fees after the trial, or harm the Service, other users, or third parties.
12.3 After termination, your right to use the Service ends. We handle retained data in accordance with applicable law and our ordinary retention practices.

## 13. Disclaimer of Warranties

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE FROM UNAUTHORIZED ACCESS.

## 14. Limitation of Liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRAVADH LABS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF DATA, REVENUE, OR PROFITS, ARISING FROM YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM, OR, IF YOU HAVE PAID NOTHING, ONE HUNDRED US DOLLARS (US$100).

## 15. Indemnification

You agree to indemnify and hold harmless Pravadh Labs from claims, damages, or expenses arising from your violation of these Terms, your misuse of the Service, or unauthorized access to any Connected System via the Service.

## 16. Changes to These Terms

We may update these Terms from time to time. Material changes will be communicated by email or in-app notice at least 14 days before they take effect. Continued use after that date is acceptance of the updated Terms.

## 17. Governing Law

These Terms are governed by the laws of India, without regard to conflict of law principles. Any disputes shall be resolved in the competent courts of India.

## 18. Contact

For questions about these Terms, contact us at legal@pravadhlabs.com.`;
